/**
 * Matomo Analytics - Cookieless tracking for GDPR compliance
 *
 * Only active in production when REACT_APP_MATOMO_URL is set.
 */

declare global {
  interface Window {
    _paq: Array<Array<string | number | boolean | undefined>>;
  }
}

const MATOMO_URL = process.env.REACT_APP_MATOMO_URL;
const MATOMO_SITE_ID = process.env.REACT_APP_MATOMO_SITE_ID || '1';

const isProduction = process.env.NODE_ENV === 'production';
const isEnabled = isProduction && !!MATOMO_URL;

/**
 * Initialize Matomo tracking (cookieless mode)
 * Call once at app startup
 */
export function initAnalytics(): void {
  if (!isEnabled) {
    console.log('[Analytics] Disabled (not production or missing REACT_APP_MATOMO_URL)');
    return;
  }

  window._paq = window._paq || [];

  // GDPR: Disable cookies entirely
  window._paq.push(['disableCookies']);

  // Outlinks and mailto: links, so the contact addresses on the public pages
  // are measured without a click handler on every one of them.
  window._paq.push(['enableLinkTracking']);

  // Without this, Matomo can only measure time on a page by the gap to the
  // NEXT page view — so every visit that reads one page and leaves is filed as
  // 0 seconds. On a marketing site that is most of the traffic, and reading
  // time is the one engagement signal a cookieless setup still gets honestly.
  window._paq.push(['enableHeartBeatTimer', 15]);

  // Configure tracker. We use Matomo's built-in /js/ proxy endpoint (it both
  // serves the tracker script and receives hits) instead of matomo.php /
  // matomo.js, so requests aren't caught by ad-blocker path filters that
  // target the default "matomo"/"piwik" file names.
  window._paq.push(['setTrackerUrl', `${MATOMO_URL}/js/`]);
  window._paq.push(['setSiteId', MATOMO_SITE_ID]);

  // Load Matomo script
  const script = document.createElement('script');
  script.async = true;
  script.src = `${MATOMO_URL}/js/`;
  document.head.appendChild(script);
}

/**
 * Track page view
 * Call on each route change
 */
export function trackPageView(path: string, title?: string): void {
  if (!isEnabled) return;

  window._paq.push(['setCustomUrl', path]);
  if (title) {
    window._paq.push(['setDocumentTitle', title]);
  }
  window._paq.push(['trackPageView']);
}

/**
 * Track custom event
 * Example: trackEvent('Hypothesis', 'Create', 'from-import')
 */
export function trackEvent(
  category: string,
  action: string,
  name?: string,
  value?: number
): void {
  if (!isEnabled) return;

  window._paq.push(['trackEvent', category, action, name, value]);
}

/**
 * Track site search
 */
export function trackSearch(
  keyword: string,
  category?: string,
  resultsCount?: number
): void {
  if (!isEnabled) return;

  window._paq.push(['trackSiteSearch', keyword, category, resultsCount]);
}

/**
 * Visit-scope custom dimension: interface language ('no' | 'en').
 *
 * Answers whether the English pages convert like the Norwegian ones — the
 * question the European push turns on. Set before the page view it belongs to,
 * or Matomo attaches it to the next hit instead.
 *
 * Requires a visit-scope custom dimension at index 1 in Matomo
 * (Administration -> Websites -> Custom Dimensions). Until it exists, Matomo
 * discards the value server-side; nothing breaks.
 */
const LANGUAGE_DIMENSION_ID = 1;

export function setVisitLanguage(language: string): void {
  if (!isEnabled) return;

  // i18n hands out regional tags ('en-GB'); the dimension only wants the base.
  window._paq.push(['setCustomDimension', LANGUAGE_DIMENSION_ID, language.split('-')[0]]);
}

/**
 * A click on a call to action on the public pages.
 *
 * `placement` is where on the page it sits ('hero', 'final-cta', 'header'),
 * never the label — labels are translated and get rewritten, and the report
 * should survive both.
 */
export function trackCtaClick(placement: string): void {
  trackEvent('Public', 'CTA', `${window.location.pathname}:${placement}`);
}

/**
 * The sign-up funnel, from the first keystroke to the account.
 *
 * Cookieless tracking cannot follow a person across days, so this measures the
 * one thing it can measure honestly: within a single visit, how far down the
 * form people get, and what stops the ones who stop.
 */
export function trackSignupStep(
  step: 'Start' | 'Invalid' | 'Failed' | 'Complete',
  detail?: string
): void {
  trackEvent('Signup', step, detail);
}
